import { Button, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { DownloadIcon } from 'lucide-react';
import { ComponentProps, FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FileDownloadFormat } from '@/shared/lib';
import { DownloadFormatBadges, IconButton, TruncatedText } from '@/shared/ui';
import type { DownloadTemplateItem } from '../../model/api/types';

/**
 * Кнопка-триггер: либо значок с названием действия, либо кнопка с видимой подписью. Они
 * взаимоисключающи по типу — подписанной кнопке не задают название, которое она не покажет.
 */
export type DownloadByTemplateTriggerProps =
  | {
      /** Название действия: и доступное имя кнопки-значка, и её подсказка — с указанием, что скачивается */
      label: string;
      trigger?: never;
    }
  | {
      /** Кнопку с видимой подписью называет сама подпись — название действия ей не задают */
      label?: never;
      /** Кнопка с подписью; доступное имя ей задаёт подпись */
      trigger: Omit<ComponentProps<typeof Button>, 'aria-label'> & { children: ReactNode };
    };

type Props = DownloadByTemplateTriggerProps & {
  /** Шаблоны сущности в порядке, в котором их отдаёт бэкенд */
  templates: DownloadTemplateItem[];
  /** Скачивание по выбранному шаблону в выбранном формате */
  onSelect: (template: DownloadTemplateItem, format: FileDownloadFormat) => void;
  /** Файл формируется — повторное скачивание не запускается */
  isPending: boolean;
  /** Классы кнопки-триггера */
  className?: string;
};

/**
 * Кнопка скачивания файлом: по клику — шаблоны сущности по их наименованиям.
 * Рядом с наименованием стоят лейблы форматов, и скачивание запускает выбор лейбла: формат
 * обязателен, поэтому у самой строки шаблона действия нет. Список раскрывается всплывающим
 * окном, а не меню: три равноправных действия в одной строке меню обходились бы только Tab'ом.
 *
 * Название действия у кнопки-значка приходит от места применения: один и тот же значок скачивает
 * то транскрипцию, то заключение, и «Скачать» на кнопке не говорит, что именно будет скачано.
 *
 * Окно прижато к правому краю значка — значки стоят у правого края своих панелей. Кнопка с подписью
 * стоит в ряду действий слева, и окно под ней раскрывается от её левого края, как меню соседних
 * действий, не наезжая на них и на навигацию.
 */
export const DownloadByTemplateMenu: FC<Props> = (props) => {
  const { templates, onSelect, isPending, className } = props;
  const { t } = useTranslation(['dialogs']);
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (template: DownloadTemplateItem, format: FileDownloadFormat) => {
    setIsOpen(false);
    onSelect(template, format);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {props.trigger === undefined ? (
        <IconButton
          size="icon"
          className={className}
          label={props.label}
          disabled={isPending}
          icon={<DownloadIcon className="size-4" />}
          trigger={PopoverTrigger}
        />
      ) : (
        <PopoverTrigger asChild>
          <Button
            icon={<DownloadIcon className="size-4" />}
            {...props.trigger}
            className={className}
            disabled={isPending || props.trigger.disabled}
          />
        </PopoverTrigger>
      )}
      <PopoverContent align={props.trigger === undefined ? 'end' : 'start'} className="flex w-80 flex-col gap-2 p-2">
        {templates.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dialogs:downloadTemplate.empty')}</p>
        ) : (
          templates.map((template) => (
            <div
              key={template.id}
              className="flex items-center gap-3"
              data-testid={`download-template-option-${template.id}`}
            >
              <TruncatedText className="text-sm">{template.name}</TruncatedText>
              <DownloadFormatBadges
                className="ml-auto"
                onSelect={(format) => handleSelect(template, format)}
                getAriaLabel={(format) => t('dialogs:downloadTemplate.asFormat', { name: template.name, format })}
              />
            </div>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};
